import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UserContext from "../../context/userContext";
import { handleLoggedInRoute } from "../../router/routeHelper";
import useJwt from "../../services/useJwt";

interface AuthTokenUser {
  roles: string[];
  fullName: string;
  email: string;
  isHost: boolean;
  userId: number;
}

interface AuthLoginResponse {
  accessToken: string;
  encryptedAccessToken: string;
  expireInSeconds: number;
  userId: number;
  refreshToken: string;
  user: AuthTokenUser;
  twoFactorRequired: boolean;
  twoFactorSetupRequired: boolean;
  setupGoogleAuthenticatorImageUrl: string | null;
  googleAuthenticatorKey: string | null;
  twoFactorType: number;
  isOfficeAccount: boolean;
}

// Custom hook to handle authentication from URL token
const useAuthFromToken = () => {
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);

  const handleOfficeLogin = async (input: AuthLoginResponse) => {
    // Normal login flow
    const data = {
      ...input.user,
      accessToken: input.accessToken,
      refreshToken: input.refreshToken,
    };

    userContext.onLoggedIn(data).then(() => {
      navigate(handleLoggedInRoute());
      setLoading(false);
    });
  };

  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleAuthentication = async () => {
      setIsProcessing(true); // Start processing

      // Get token from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get("token");

      if (token) {
        try {
          // Make API call to external-authenticate with the token
          const response = await useJwt.externalAuthenticate({ token });
          console.log("External authenticate response:", response);

          if (response.data) {
            const authData = response.data.result;
            handleOfficeLogin(authData);
          }
        } catch (error) {
          console.error("External authentication failed:", error);
          // Redirect to external login failed page
          navigate("/external-login/failed");
        }
      }

      setIsProcessing(false); // End processing
    };

    handleAuthentication();
  }, [location.search]); // Re-run when URL parameters change

  return { isProcessing };
};

export default useAuthFromToken;
